import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { InfoSectionContainer } from '@/common/InfoSection';
import { WBSButton } from '@/common/WBSButton';
import { Avatar } from '@/header/Avatar';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { StyledTextarea } from '~/styles/forms/formElements';

interface NoteInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  /**
   * A prop, unlike the avatar below: the composer's label is the logged-in
   * user's display name (`buddypress/members/single/student-notes.php:16`) and
   * the student-notes screen passes `me?.display_name ?? ''`, but a caller with
   * a different source for it does not have to fight the hook.
   */
  userName: string;
  /**
   * The textarea's own copy, which is the only statement of what a note is for
   * and differs per screen — "about this student" vs "about this group".
   */
  placeholder: string;
  /**
   * Optional: true while the caller's create is in flight, which is the second
   * of the two submit guards below. Defaults to false, so a caller whose submit
   * resolves synchronously renders unchanged.
   */
  isSubmitting?: boolean;
}

export function NoteInput({
  value,
  onChange,
  onSubmit,
  userName,
  placeholder,
  isSubmitting = false,
}: NoteInputProps) {
  const { t } = useTranslation();
  const { me } = useCurrentUser();
  const memberId = me?.id?.toString() || '';

  return (
    <NoteInputContainer>
      <AvatarNameRow to={`/members/${memberId}`}>
        {/* Derived, not a prop: the composer's author is always the logged-in
            user, which is what legacy's `bp_loggedin_user_avatar` means
            (`buddypress/groups/single/group-notes.php:12-15`), and a prop would
            let a caller pass an avatar contradicting the profile link it sits
            in. `NoteItem`'s avatar IS a prop because its author varies per row. */}
        <Avatar size={50} src={me?.avatar} />
        <Name>{userName}</Name>
      </AvatarNameRow>
      <FullWidthTextarea
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <ButtonRow>
        {/* `disabled` matches the server rule: content is trimmed before
            `min_length=1`, so whitespace-only is a 422 the button never lets us
            send (D4). `isLoading` is the second guard and covers a different
            case — the create carries no idempotency key and the server accepts
            identical content twice, so without it a double-click writes two
            notes. */}
        <WBSButton
          wide
          onClick={onSubmit}
          disabled={!value.trim()}
          isLoading={isSubmitting}
        >
          {t('actions.add-note')}
        </WBSButton>
      </ButtonRow>
    </NoteInputContainer>
  );
}

const NoteInputContainer = styled(InfoSectionContainer)`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 21px;
  padding: 12px;
`;

const Name = styled.h2`
  color: var(--grey-900);
  font-family: var(--raleway);
  font-size: 14px;
  font-weight: 300;
  margin: 0;
`;

const AvatarNameRow = styled(Link)`
  align-items: center;
  align-self: flex-start;
  cursor: pointer;
  display: flex;
  gap: 12px;
  text-decoration: none;

  &:hover ${Name} {
    color: var(--wbs-red);
  }
`;

const FullWidthTextarea = styled(StyledTextarea)`
  font-size: 16px;
  font-weight: 300;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;
